import {
  CalendarToday as CalendarTodayIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import { TabContext } from '@mui/lab';
import {
  ClickAwayListener,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import _, { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyState from 'src/assets/images/EmptyState.svg';
import {
  CALENDAR_TAB_VALUES,
  ECalendarEventType,
} from 'src/components/Calendar/Calendar.interface';
import { Drawer } from 'src/components/drawer';
import { IconTypography } from 'src/components/iconTypography/IconTypography';
import { SimpleDialog } from 'src/components/modal';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  COLLECTIONS,
  DEFAULT_POSITION_ALL,
  US_DATE_FORMAT,
  USER_ACTIONS,
} from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  LightTypography,
  RegularTypography,
} from 'src/features/locations/calendarLocationGroupPage/modules/CalendarView/CalendarView.styled';
import { useModal } from 'src/hooks';
import { useDeleteScheduleItem } from 'src/hooks/api/schedules/useDeleteScheduleItem';
import { useDeleteScheduleItemOverrides } from 'src/hooks/api/schedules/useDeleteScheduleItemOverrides';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { lang, ScheduleDetailsParams, theme, useUserRole } from 'src/utils';

import {
  getMenuBoardModificationNavigation,
  getScheduleDetails,
  isOnlyItemRemaining,
  isPreviewEnabled,
  openSchedulePreview,
} from './EventContentSidebar.helper';
import {
  DefaultAssemblyScheduleItem,
  IEventContentSidebar,
  Position,
  ScheduleComponentEvent,
  ScheduleComponentItem,
} from './EventContentSidebar.interface';
import {
  BottomPanel,
  ContentPanel,
  CustomTab,
  CustomTabList,
  CustomTabPanel,
  DeleteIconButton,
  DeleteScheduleItemButton,
  EditIconButton,
  EmptyStateWrapper,
  FixedWidthColumn,
  Header,
  HeaderText,
  LightBlueLink,
  NoBackupImage,
  OverrideIconButton,
  PreviewButton,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledGrid,
  StyledLink,
  StyledLocationOverrideChip,
  StyledPeopleIcon,
  StyledPointerText,
  VerticalSpacedContainer,
  ViewContentButton,
} from './EventContentSidebar.styled';

const DeleteScheduleItemModalSubTitle = ({
  currentDeletingScheduleItem,
  hasLocations,
}: {
  currentDeletingScheduleItem: ScheduleComponentItem | undefined;
  hasLocations: boolean;
}) => {
  if (!currentDeletingScheduleItem) return null;

  const deleteScheduleItemSubTitleLines = hasLocations
    ? lang.deleteScheduleItemModal.subTitleWithOverridesWarning(
        currentDeletingScheduleItem?.componentName as string,
      )
    : lang.deleteScheduleItemModal.subTitleWithOutOverridesWarning(
        currentDeletingScheduleItem?.componentName as string,
      );

  return (
    <>
      {deleteScheduleItemSubTitleLines.map((s, index) => (
        <span
          key={index}
          {...(index === 0
            ? {
                style: { marginBottom: '18px', display: 'block' },
              }
            : {})}>
          {s}
        </span>
      ))}
    </>
  );
};

export const EventContentSidebar = (props: IEventContentSidebar) => {
  const {
    selectedEvent,
    position,
    open,
    close,
    locationGroupId,
    isLocationGroupView,
    locationCode,
    locationName,
    locationGroupName,
    channelName,
    refreshEventData,
    refreshSidebarData,
    showPublicationModal,
    currentSidebarDetailsDataParams,
    deleteBundleAction,
    locationsCount,
  } = props;

  const navigate = useNavigate();
  const { isOwnerOperator, isViewOnly } = useUserRole();
  const [selectedTab, setSelectedTab] = useState<CALENDAR_TAB_VALUES>(
    CALENDAR_TAB_VALUES.EVENT_CONTENT,
  );
  const [currentDeletingScheduleItem, setCurrentDeletingScheduleItem] =
    useState<ScheduleComponentItem>();
  const [expandedAccordions, setExpandedAccordions] = useState<{
    [key: string]: boolean;
  }>({});
  const showDeleteBundleModal = useModal();
  const showDeleteScheduleItemOverridesModal = useModal();
  const showDeleteScheduleItemModal = useModal();
  // delete schedule item override
  const deleteScheduleItemOverrides = useDeleteScheduleItemOverrides();
  // delete schedule item
  const deleteScheduleItem = useDeleteScheduleItem();

  const isDefaultAssembly =
    selectedEvent?.eventType === ECalendarEventType.defaultAssembly;
  const showCMActionButton =
    !isViewOnly && !isOwnerOperator && !isDefaultAssembly;
  const showOOActionButton = isOwnerOperator && !isDefaultAssembly;
  // only CM in LG sidebar can delete schedule item
  const showLGDeleteScheduleItemButton =
    !isOwnerOperator && !isViewOnly && isLocationGroupView;

  const handleOOOverrideClick = () => {
    if (!locationCode || !channelName || !locationGroupId) return;

    const { menuBoardPath, navigateBackLink } =
      getMenuBoardModificationNavigation({
        selectedEvent,
        position,
        locationCode,
        channelName,
        locationGroupId,
        daypartId: props.daypartId,
      });

    navigate(menuBoardPath, {
      state: {
        navigateBackLink,
      },
    });
  };

  const allBackupsEmpty = isDefaultAssembly
    ? _.every(
        selectedEvent?.scheduleItems as DefaultAssemblyScheduleItem[],
        (item) => _.isEmpty(item.backups),
      )
    : _.every(selectedEvent?.positions as Position[], (pos) =>
        _.every(pos.scheduleItems, (item) => _.isEmpty(item.backups)),
      );
  const scheduledItemArray = useMemo(
    () => getScheduleDetails(selectedEvent),
    [selectedEvent],
  );

  useEffect(() => {
    // Skip accordion management until sidebar is fully open
    if (!open) return;

    setExpandedAccordions((prev) => {
      const newExpanded = { ...prev };

      if (position !== DEFAULT_POSITION_ALL && scheduledItemArray) {
        // When viewing a specific position, expand all accordions for that position
        (scheduledItemArray as Position[]).forEach((pos) => {
          newExpanded[pos.number] = true;
        });
      } else if (scheduledItemArray) {
        // When viewing all positions, preserve user's expanded state but remove deleted positions
        const existingPositionNumbers = new Set(
          (scheduledItemArray as Position[]).map((pos) => pos.number),
        );
        Object.keys(newExpanded).forEach((key) => {
          if (!existingPositionNumbers.has(Number(key))) {
            delete newExpanded[key];
          }
        });
      }

      return newExpanded;
    });
  }, [open, position, scheduledItemArray]);

  const handleAccordionToggle = (positionNumber: number) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [positionNumber]: !prev[positionNumber],
    }));
  };
  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: CALENDAR_TAB_VALUES,
  ) => {
    setSelectedTab(newValue);
  };

  const handleSchedulePreview = () => {
    if (!locationGroupId || !props.daypartId) return;

    openSchedulePreview({
      locationGroupId,
      daypartId: props.daypartId,
      date: props.selectedEvent?.start,
    });
  };

  const getScheduleDateLabel = (
    start?: string | Date | null,
    end?: string | Date | null,
  ) => {
    const startValid = start && dayjs(start).isValid();
    const endValid = end && dayjs(end).isValid();

    if (!startValid && !endValid) return '';

    if (startValid && endValid) {
      return `${dayjs(start!).format(US_DATE_FORMAT)} - ${dayjs(end!).format(
        US_DATE_FORMAT,
      )}`;
    }

    if (startValid) {
      return dayjs(start!).format(US_DATE_FORMAT);
    }

    if (endValid) {
      return dayjs(end!).format(US_DATE_FORMAT);
    }

    return '';
  };

  const handleDeleteScheduleBundle = () => {
    showDeleteBundleModal.onOpenModal(async () => {
      if (
        selectedEvent?.eventType === ECalendarEventType.scheduleComponent &&
        selectedEvent?.scheduleId
      ) {
        const narrative = locationCode
          ? narrativeGenerators[COLLECTIONS.SCHEDULE_COMPONENTS][
              USER_ACTIONS.DELETE
            ](locationName ?? '', selectedEvent.title ?? '')
          : narrativeGenerators[COLLECTIONS.SCHEDULE_COMPONENTS][
              USER_ACTIONS.DELETE
            ](locationGroupName ?? '', selectedEvent.title ?? '');
        const params = locationCode
          ? {
              scheduleId: selectedEvent.scheduleId,
              locationCode: locationCode,
              narrative,
            }
          : {
              scheduleId: selectedEvent.scheduleId,
              narrative,
              locationGroupId,
            };

        await deleteBundleAction.mutateAsync(params);

        showDeleteBundleModal.onCloseModal();

        refreshEventData();

        // Always close the sidebar - deleting the entire schedule means there's
        // nothing left to show, so no need to call refreshSidebarData
        close();

        if ((locationsCount ?? 0) > 0) {
          showPublicationModal.onOpenModal();
        }
      }
    });
  };

  const handleDeleteScheduleItemOverrides = async (
    scheduleItemId: number,
    currentPosition: number,
  ) => {
    showDeleteScheduleItemOverridesModal.onOpenModal(async () => {
      await deleteScheduleItemOverrides.mutateAsync({
        scheduleItemId,
        currentSidebarDetailsDataParams,
        currentPosition,
        locationGroupId,
      });

      showDeleteScheduleItemOverridesModal.onCloseModal();

      refreshEventData(); // update calendar grid
      await refreshSidebarData(); // update sidebar

      if ((locationsCount ?? 0) > 0) {
        showPublicationModal.onOpenModal();
      }
    });
  };

  const handleTabResetOnClose = () => {
    if (open) {
      close();
    } else {
      setSelectedTab(CALENDAR_TAB_VALUES.EVENT_CONTENT);
    }
  };

  const handleDeleteScheduleItem = (
    scheduleItem: ScheduleComponentItem,
    currentPosition: number,
  ) => {
    setCurrentDeletingScheduleItem(scheduleItem);

    showDeleteScheduleItemModal.onOpenModal(async () => {
      await deleteScheduleItem.mutateAsync({
        scheduleItemId: scheduleItem.id,
        currentSidebarDetailsDataParams:
          currentSidebarDetailsDataParams as ScheduleDetailsParams,
        currentPosition,
        narrative: narrativeGenerators[COLLECTIONS.SCHEDULE_ITEMS][
          USER_ACTIONS.DELETE
        ](
          locationGroupName ?? '',
          scheduleItem.componentName,
          selectedEvent?.title ?? '',
        ),
        locationGroupId,
      });

      showDeleteScheduleItemModal.onCloseModal();

      refreshEventData();

      // If this was the last item, close the drawer without calling the API
      // (BE removes the scheduleId when the last item is deleted)
      if (isOnlyItemRemaining(scheduledItemArray)) {
        close();
      } else {
        await refreshSidebarData(); // update sidebar to remove the deleted item
      }

      if ((locationsCount ?? 0) > 0) {
        showPublicationModal.onOpenModal();
      }
    });
  };

  const handleEditClick = () => {
    const scheduleId = (selectedEvent as ScheduleComponentEvent).scheduleId;
    navigate({
      pathname: PATHS.LOCATIONS_SCHEDULE_COMPONENTS,
      search: `?scheduleId=${scheduleId}`,
    });
  };
  const isPreviewAllowed = useMemo(() => {
    return isPreviewEnabled(selectedEvent?.start, selectedEvent?.end);
  }, [selectedEvent?.end, selectedEvent?.start]);

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={handleTabResetOnClose}>
      <Drawer open={open} anchor="right" width="unset">
        <FixedWidthColumn>
          {/* Schedule Component Header Title */}
          <Header $isDefaultAssembly={isDefaultAssembly}>
            <CustomTooltip
              width={200}
              placement="bottom"
              title={selectedEvent?.title}
              overflowWidth={180}>
              <HeaderText>{selectedEvent?.title}</HeaderText>
            </CustomTooltip>
            <IconButton data-testid="close-icon" onClick={close}>
              <CloseIcon />
            </IconButton>
          </Header>

          {/* Schedule Dates */}
          <Stack direction="row" spacing={13}>
            {/* Start Date */}
            <Stack direction="column">
              <RegularTypography>{lang.startDate}</RegularTypography>
              <IconTypography
                icon={<CalendarTodayIcon />}
                text={getScheduleDateLabel(selectedEvent?.start)}
                textFontSize="16px"
                textFontWeight="300"
                textColor={theme.palette.brand.black}
                iconSize="16px"
                iconColor={theme.palette.grey[700]}
              />
            </Stack>

            {/* End Date */}
            <Stack direction="column">
              <RegularTypography>{lang.endDate}</RegularTypography>
              {selectedEvent?.end ? (
                <IconTypography
                  icon={<CalendarTodayIcon />}
                  text={getScheduleDateLabel(selectedEvent.end)}
                  textFontSize="16px"
                  textFontWeight="300"
                  textColor={theme.palette.brand.black}
                  iconSize="16px"
                  iconColor={theme.palette.grey[700]}
                />
              ) : (
                <IconTypography
                  icon={<CalendarTodayIcon />}
                  text={lang.noEndDate}
                  textFontSize="16px"
                  textFontWeight="300"
                  textColor={theme.palette.brand.black}
                  iconSize="16px"
                  iconColor={theme.palette.grey[700]}
                />
              )}
            </Stack>
          </Stack>

          {/* Schedule Modified Date */}
          <Stack direction="column">
            {dayjs(selectedEvent?.modifiedDate).isValid() && (
              <>
                <RegularTypography>{lang.modifiedDate}</RegularTypography>
                <IconTypography
                  icon={<CalendarTodayIcon />}
                  text={getScheduleDateLabel(selectedEvent?.modifiedDate)}
                  textFontSize="16px"
                  textFontWeight="300"
                  textColor={theme.palette.brand.black}
                  iconSize="16px"
                  iconColor={theme.palette.grey[700]}
                />
              </>
            )}
          </Stack>

          {/* Tabs*/}
          <ContentPanel direction="column">
            <TabContext value={selectedTab}>
              <CustomTabList onChange={handleTabChange}>
                <CustomTab
                  label={isDefaultAssembly ? lang.details : lang.components}
                  value={CALENDAR_TAB_VALUES.EVENT_CONTENT}
                />
                <CustomTab
                  label={lang.backup}
                  value={CALENDAR_TAB_VALUES.BACKUP}
                />
              </CustomTabList>

              {/* Components/Details Tab Content */}
              <CustomTabPanel value={CALENDAR_TAB_VALUES.EVENT_CONTENT}>
                {isDefaultAssembly ? (
                  <LightBlueLink>
                    <CustomTooltip
                      width={200}
                      placement="bottom"
                      title={selectedEvent?.assemblyName}
                      overflowWidth={180}>
                      <StyledLink
                        to={`${PATHS.ASSEMBLY_DETAILS(
                          selectedEvent?.assemblyId,
                        )}`}
                        target="_blank">
                        {selectedEvent?.assemblyName}
                      </StyledLink>
                    </CustomTooltip>
                  </LightBlueLink>
                ) : (
                  <Stack direction="column">
                    {(scheduledItemArray as Position[])?.map((pos, idx) => (
                      <StyledAccordion
                        key={idx}
                        expanded={!!expandedAccordions[pos.number]}
                        onChange={() => handleAccordionToggle(pos.number)}>
                        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography>{`Position ${pos.number}`}</Typography>
                        </StyledAccordionSummary>
                        {pos.scheduleItems?.map(
                          (item: ScheduleComponentItem, i) => (
                            <StyledAccordionDetails key={i}>
                              <VerticalSpacedContainer
                                container
                                alignItems="top">
                                {item.allowOverrides && (
                                  <Grid item spacing={1}>
                                    <CustomTooltip
                                      placement="top"
                                      title={
                                        isOwnerOperator
                                          ? lang.ooOverrideEnabled
                                          : lang.cmOverrideEnabled
                                      }
                                      skipValidateOverflow={true}>
                                      <StyledPeopleIcon data-testid="overridable icon" />
                                    </CustomTooltip>
                                  </Grid>
                                )}

                                <Grid item>
                                  <CustomTooltip
                                    width={200}
                                    placement="bottom"
                                    title={item.componentName}
                                    overflowWidth={180}>
                                    <StyledLink
                                      to={`${PATHS.COMPONENT_DETAILS(
                                        item.componentId,
                                      )}`}
                                      target="_blank">
                                      {item.componentName}
                                    </StyledLink>
                                  </CustomTooltip>
                                </Grid>

                                {(showLGDeleteScheduleItemButton ||
                                  (isOwnerOperator &&
                                    !isEmpty(item.overrides))) && (
                                  <DeleteScheduleItemButton
                                    data-testid="delete-schedule-item"
                                    onClick={() => {
                                      showLGDeleteScheduleItemButton
                                        ? handleDeleteScheduleItem(
                                            item,
                                            pos.number,
                                          )
                                        : handleDeleteScheduleItemOverrides(
                                            item.id,
                                            pos.number,
                                          );
                                    }}>
                                    <DeleteOutlineIcon aria-label="delete schedule item" />
                                  </DeleteScheduleItemButton>
                                )}

                                <Grid item xs={12}>
                                  <LightTypography>
                                    {getScheduleDateLabel(item.start, item.end)}
                                  </LightTypography>
                                </Grid>

                                {/* only OO can delete the override items */}
                              </VerticalSpacedContainer>

                              {/* both CM individual loc and OO can see the override items */}
                              {!isLocationGroupView &&
                                item.overrides?.map((overrideItem: any) => (
                                  <StyledGrid
                                    container
                                    alignItems="top"
                                    key={overrideItem.id}>
                                    <Grid item xs={8}>
                                      <CustomTooltip
                                        width={200}
                                        placement="bottom"
                                        title={overrideItem.title}
                                        overflowWidth={180}>
                                        {overrideItem.componentId === null ? (
                                          <Typography>
                                            {overrideItem.title}
                                          </Typography>
                                        ) : (
                                          <StyledLink
                                            to={`${PATHS.COMPONENT_DETAILS(
                                              overrideItem.componentId,
                                            )}`}
                                            target="_blank">
                                            {overrideItem.title}
                                          </StyledLink>
                                        )}
                                      </CustomTooltip>

                                      <LightTypography>
                                        {getScheduleDateLabel(
                                          overrideItem.start,
                                          overrideItem.end,
                                        )}
                                      </LightTypography>
                                    </Grid>
                                    <Grid item xs={4}>
                                      <StyledLocationOverrideChip
                                        chipName={lang.ownerOperatorOverride}
                                      />
                                    </Grid>
                                  </StyledGrid>
                                ))}
                            </StyledAccordionDetails>
                          ),
                        )}
                      </StyledAccordion>
                    ))}
                  </Stack>
                )}
              </CustomTabPanel>

              {/* Backups Tab Content */}
              <CustomTabPanel value={CALENDAR_TAB_VALUES.BACKUP}>
                {allBackupsEmpty ? (
                  <EmptyStateWrapper>
                    <NoBackupImage
                      src={EmptyState}
                      alt="empty backup state"
                      data-testid="empty-state"
                      draggable={false}
                    />
                    <Typography variant="body2">{lang.noBackups}</Typography>
                  </EmptyStateWrapper>
                ) : (
                  <Stack direction="column">
                    {isDefaultAssembly
                      ? (scheduledItemArray as DefaultAssemblyScheduleItem[])
                          .filter((item) => item.backups.length > 0)
                          .map((item, i) => (
                            <StyledAccordion key={`${item.id}-${i}`}>
                              <StyledAccordionSummary
                                expandIcon={<ExpandMoreIcon />}>
                                <CustomTooltip
                                  width={200}
                                  placement="bottom"
                                  title={item.componentName}
                                  overflowWidth={180}>
                                  <StyledPointerText>
                                    {item.componentName}
                                  </StyledPointerText>
                                </CustomTooltip>
                              </StyledAccordionSummary>
                              {item.backups.map((backup, j) => (
                                <StyledAccordionDetails key={j}>
                                  <VerticalSpacedContainer>
                                    <CustomTooltip
                                      width={200}
                                      placement="bottom"
                                      title={backup.componentName}
                                      overflowWidth={180}>
                                      <StyledLink
                                        to={PATHS.COMPONENT_DETAILS(
                                          backup.componentId,
                                        )}
                                        target="_blank">
                                        {backup.componentName}
                                      </StyledLink>
                                    </CustomTooltip>
                                  </VerticalSpacedContainer>
                                </StyledAccordionDetails>
                              ))}
                            </StyledAccordion>
                          ))
                      : (scheduledItemArray as Position[]).map(
                          (pos, positionIndex) => {
                            const componentAccordions = pos.scheduleItems
                              .filter((item) => item.backups.length > 0)
                              .map((item, i) => (
                                <StyledAccordion
                                  key={`${item.id}-${i}`}
                                  expanded={!!expandedAccordions[pos.number]}
                                  onChange={() =>
                                    handleAccordionToggle(pos.number)
                                  }>
                                  <StyledAccordionSummary
                                    expandIcon={<ExpandMoreIcon />}>
                                    <CustomTooltip
                                      width={200}
                                      placement="bottom"
                                      title={item.componentName}
                                      overflowWidth={180}>
                                      <StyledPointerText>
                                        {item.componentName}
                                      </StyledPointerText>
                                    </CustomTooltip>
                                  </StyledAccordionSummary>
                                  {item.backups.map((backup, j) => (
                                    <StyledAccordionDetails key={j}>
                                      <VerticalSpacedContainer>
                                        <CustomTooltip
                                          width={200}
                                          placement="bottom"
                                          title={backup.componentName}
                                          overflowWidth={180}>
                                          <StyledLink
                                            to={PATHS.COMPONENT_DETAILS(
                                              backup.componentId,
                                            )}
                                            target="_blank">
                                            {backup.componentName}
                                          </StyledLink>
                                        </CustomTooltip>
                                      </VerticalSpacedContainer>
                                    </StyledAccordionDetails>
                                  ))}
                                </StyledAccordion>
                              ));

                            if (componentAccordions.length === 0) return null;

                            return (
                              <StyledAccordion
                                key={positionIndex}
                                expanded={!!expandedAccordions[pos.number]}
                                onChange={() =>
                                  handleAccordionToggle(pos.number)
                                }>
                                <StyledAccordionSummary
                                  expandIcon={<ExpandMoreIcon />}>
                                  <Typography>{`Position ${pos.number}`}</Typography>
                                </StyledAccordionSummary>
                                <Stack direction="column">
                                  {componentAccordions}
                                </Stack>
                              </StyledAccordion>
                            );
                          },
                        )}
                  </Stack>
                )}
              </CustomTabPanel>
            </TabContext>
          </ContentPanel>

          {/* Bottom Actions Panel */}
          <BottomPanel>
            {showCMActionButton ? (
              <>
                <>
                  <EditIconButton
                    disabled={isViewOnly || !isLocationGroupView}
                    onClick={handleEditClick}>
                    <EditIcon />
                  </EditIconButton>

                  <DeleteIconButton
                    variant="outlined"
                    data-testid="delete-schedule"
                    onClick={handleDeleteScheduleBundle}
                    disabled={isViewOnly}>
                    <DeleteOutlineIcon aria-label="delete schedule" />
                    {lang.deleteSchedule}
                  </DeleteIconButton>
                </>
              </>
            ) : showOOActionButton ? (
              <>
                <OverrideIconButton
                  variant="outlined"
                  startIcon={<EditIcon />}
                  disabled={!selectedEvent?.hasOverridableComponent}
                  onClick={handleOOOverrideClick}>
                  {lang.override}
                </OverrideIconButton>
                <ViewContentButton>{lang.viewContent}</ViewContentButton>
              </>
            ) : (
              <ViewContentButton flex={1}>{lang.viewContent}</ViewContentButton>
            )}
            {isLocationGroupView && (
              <PreviewButton
                variant="contained"
                startIcon={<PlayCircleOutlinedIcon />}
                onClick={handleSchedulePreview}
                flex={1}
                disabled={!isPreviewAllowed}>
                {lang.preview}
              </PreviewButton>
            )}
          </BottomPanel>
        </FixedWidthColumn>
        <SimpleDialog
          modalSize="sm"
          open={showDeleteBundleModal.open}
          title={lang.deleteScheduleComponentModal.title}
          subTitle={
            (locationsCount ?? 0) > 0
              ? lang.deleteScheduleComponentModal.subTitleWithOverridesWarning(
                  selectedEvent?.title,
                )
              : lang.deleteScheduleComponentModal.subTitleWithOutOverridesWarning(
                  selectedEvent?.title,
                )
          }
          actionColor="error"
          confirmText={lang.delete}
          cancelText={lang.cancel}
          isLoading={deleteBundleAction.isLoading}
          onClickConfirm={showDeleteBundleModal.customFn}
          onClickCancel={showDeleteBundleModal.onCloseModal}
          onClose={showDeleteBundleModal.onCloseModal}
        />
        {showDeleteScheduleItemOverridesModal.open && (
          <SimpleDialog
            modalSize="sm"
            open={showDeleteScheduleItemOverridesModal.open}
            title={lang.deleteScheduleItemOverridesModal.title}
            subTitle={lang.deleteScheduleItemOverridesModal.subTitle}
            actionColor="error"
            confirmText={lang.delete}
            cancelText={lang.cancel}
            isLoading={deleteScheduleItemOverrides.isLoading}
            onClickConfirm={showDeleteScheduleItemOverridesModal.customFn}
            onClickCancel={showDeleteScheduleItemOverridesModal.onCloseModal}
            onClose={showDeleteScheduleItemOverridesModal.onCloseModal}
          />
        )}
        {showDeleteScheduleItemModal.open && (
          <SimpleDialog
            modalSize="sm"
            open={showDeleteScheduleItemModal.open}
            title={lang.deleteScheduleItemModal.title}
            subTitle={
              <DeleteScheduleItemModalSubTitle
                currentDeletingScheduleItem={currentDeletingScheduleItem}
                hasLocations={(locationsCount ?? 0) > 0}
              />
            }
            actionColor="error"
            confirmText={lang.delete}
            cancelText={lang.cancel}
            isLoading={deleteScheduleItem.isLoading}
            onClickConfirm={showDeleteScheduleItemModal.customFn}
            onClickCancel={showDeleteScheduleItemModal.onCloseModal}
            onClose={showDeleteScheduleItemModal.onCloseModal}
          />
        )}
      </Drawer>
    </ClickAwayListener>
  );
};
